import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  ArrowDown,
  ArrowUp,
  Bell,
  Check,
  Clock,
  GitBranch,
  MoreHorizontal,
  Pencil,
  Plus,
  Target,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { Input } from '@/components/atoms/input';
import { NumberInput } from '@/components/molecules/NumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/sheet';
import { Skeleton } from '@/components/atoms/skeleton';
import { Switch } from '@/components/atoms/switch';
import { DetailPageLayout } from '@/components/layouts';
import {
  CollapsibleSectionHeader,
  DataTableDrawerHeader,
  DataTableSearchFilterField,
  DataTableSelectFilterField,
  FormAccordionCard,
  FormOptionalSectionDivider,
  RoutePermissionDenied,
} from '@/components/molecules';
import { StatusChip } from '@/components/molecules/StatusChip';
import {
  DataTableFilterSection,
  SettingsDataGrid,
  type SettingsDataGridColumn,
  type SettingsDataGridSortDir,
} from '@/components/organisms';
import { Fullbleed } from '@/contexts/fullbleed';
import { useCreateLeadStage } from '@/hooks/leads/useCreateLeadStage';
import { useDeleteLeadStage } from '@/hooks/leads/useDeleteLeadStage';
import { useLeadSettings, type LeadSettings } from '@/hooks/leads/useLeadSettings';
import { useLeadStages } from '@/hooks/leads/useLeadStages';
import { useReorderLeadStages } from '@/hooks/leads/useReorderLeadStages';
import { useUpdateLeadPipelineStage } from '@/hooks/leads/useUpdateLeadPipelineStage';
import { useUpdateLeadSettings } from '@/hooks/leads/useUpdateLeadSettings';
import { useLeadOwners } from '@/hooks/leads/useLeadOwners';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import { useHasPermission } from '@/hooks/usePermissions';
import { LEAD_SOURCES, leadSourceLabel } from '@/lib/constants/lead-sources';
import { AcquisitionChannelSettingsSection } from '@/components/organisms/leads/AcquisitionChannelSettingsSection';
import { LeadRoutingRulesSection } from '@/components/organisms/leads/LeadRoutingRulesSection';
import { LeadsEngineTabs } from '@/components/organisms/leads/LeadsEngineTabs';
import { LeadRepAvailabilitySection } from '@/components/organisms/leads/LeadRepAvailabilitySection';
import { LeadTestIntakePanel } from '@/components/organisms/leads/LeadTestIntakePanel';
import type { LeadStage } from '@/types/lead';
import {
  TERMINAL_MODES,
  TERMINAL_MODE_DESCRIPTIONS,
  TERMINAL_MODE_LABELS,
  type TerminalMode,
  getTerminalMode,
  terminalFlags,
} from '@/components/organisms/leads/support/terminal-mode';

type DrawerState = 'settings' | 'stage-new' | 'stage-edit' | 'stage-detail';
type StageColumn = 'stage' | 'type' | 'default' | 'active' | 'sla' | 'order';
// Derived, not re-listed. A hand-copied union drifts the moment a terminal is
// added — which is exactly how the disqualified split came to be invisible here.
type StageTypeFilter = 'all' | TerminalMode;
type StageStatusFilter = 'all' | 'active' | 'inactive';

interface LeadSettingsSearch {
  tab?: 'test';
  drawer?: DrawerState;
  stageId?: string;
}

export const Route = createFileRoute('/_dashboard/dashboard/settings/leads')({
  validateSearch: (search: Record<string, unknown>): LeadSettingsSearch => ({
    tab: search.tab === 'test' ? 'test' : undefined,
    drawer:
      search.drawer === 'settings' ||
      search.drawer === 'stage-new' ||
      search.drawer === 'stage-edit' ||
      search.drawer === 'stage-detail'
        ? search.drawer
        : undefined,
    stageId: typeof search.stageId === 'string' ? search.stageId : undefined,
  }),
  component: LeadSettingsPage,
});

const PAGE_SIZE = 25;
const NO_ESCALATION_VALUE = '__none__';
const DEFAULT_SETTINGS: LeadSettings = {
  tenantId: '',
  defaultFollowUpDays: 2,
  autoSetFollowUp: true,
  defaultSlaMinutes: 5,
  slaUrgentMinutes: 3,
  slaHighMinutes: 5,
  slaLowMinutes: 15,
  slaWarningThresholdPercent: 80,
  slaBreachAction: 'NOTIFY_ONLY',
  slaEscalationUserId: null,
  sourceSlaOverrides: [],
  autoAssignEnabled: false,
  fallbackUserId: null,
  repCapacityLimit: 20,
  honorWeeklyHours: false,
  categoryRoutes: [],
  territoryRoutes: [],
  sourceRoutes: [],
  notifyOnNewLead: true,
  notifyOnSlaWarning: true,
  notifyOnSlaBreach: true,
};

const STAGE_COLUMNS: SettingsDataGridColumn<StageColumn>[] = [
  { key: 'stage', label: 'Stage', sortable: true, width: 360 },
  { key: 'type', label: 'Type', sortable: true, width: 140 },
  { key: 'default', label: 'Default', sortable: true, align: 'center', width: 120 },
  { key: 'active', label: 'Active', sortable: true, align: 'center', width: 120 },
  { key: 'sla', label: 'SLA', sortable: true, align: 'center', width: 120 },
  { key: 'order', label: 'Order', sortable: true, align: 'center', width: 100 },
];

/**
 * What each terminal mode is called on screen, in one place.
 *
 * Three things read it — the filter dropdown, the Type badge on each row, and
 * the row search, which builds its haystack from the same label the badge
 * renders. A second copy of the wording splits them: the screen filters by one
 * word and displays another, and searching the visible text finds nothing.
 *
 * Suite F (F-03) — a lead converts, it is never "Won"; Won is an estimate
 * concept. The `won` *key* stays as it is, along with the `isWon` column behind
 * it: this is copy, not the data model.
 */
// Built from the mode list rather than typed out beside it. Enumerating them
// here is what let the board offer three filters for four terminals.
const STAGE_TYPE_OPTIONS: { value: StageTypeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  ...TERMINAL_MODES.map((mode) => ({
    value: mode,
    label: TERMINAL_MODE_LABELS[mode],
  })),
];

const STAGE_STATUS_OPTIONS: { value: StageStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// Deferred-apply drawer filters (NOT-391): the Usage selects edit a local draft;
// filtering only changes on Apply. Text search stays instant (not drafted).
interface StageFilterDraft {
  search: string;
  type: StageTypeFilter;
  status: StageStatusFilter;
}

const STAGE_FILTER_DEFAULTS: StageFilterDraft = { search: '', type: 'all', status: 'all' };

const settingsSchema = z
  .object({
    defaultFollowUpDays: z.number().int().min(1).max(90),
    autoSetFollowUp: z.boolean(),
    defaultSlaMinutes: z.number().int().min(1),
    slaUrgentMinutes: z.number().int().min(1),
    slaHighMinutes: z.number().int().min(1),
    slaLowMinutes: z.number().int().min(1),
    slaWarningThresholdPercent: z.number().int().min(1).max(99),
    slaBreachAction: z.enum(['NOTIFY_ONLY', 'REASSIGN']),
    slaEscalationUserId: z.string().nullable(),
    sourceSlaOverrides: z.array(
      z.object({
        source: z.string().min(1, 'Choose a source.'),
        slaMinutes: z.number().int().min(1),
      }),
    ),
    notifyOnNewLead: z.boolean(),
    notifyOnSlaWarning: z.boolean(),
    notifyOnSlaBreach: z.boolean(),
    autoAssignEnabled: z.boolean(),
    fallbackUserId: z.string().nullable(),
    repCapacityLimit: z.number().int().min(1),
    honorWeeklyHours: z.boolean(),
  })
  // NOT-93: A fallback user is required whenever auto-assign is on.
  // The backend already enforces this with a 400; this keeps the UI honest.
  .superRefine((values, ctx) => {
    if (values.autoAssignEnabled && !values.fallbackUserId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fallbackUserId'],
        message: 'A fallback user is required when auto-assign is on.',
      });
    }
    if (
      values.slaBreachAction === 'REASSIGN' &&
      !values.fallbackUserId &&
      !values.slaEscalationUserId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['slaEscalationUserId'],
        message: 'Choose an escalation or fallback user before enabling reassign.',
      });
    }

    const sources = new Set<string>();
    values.sourceSlaOverrides.forEach((override, index) => {
      if (sources.has(override.source)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sourceSlaOverrides', index, 'source'],
          message: 'Each source can only have one SLA override.',
        });
      }
      sources.add(override.source);
    });
  });

type LeadSettingsFormValues = z.infer<typeof settingsSchema>;

const stageSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Use a valid hex color'),
  terminalMode: z.enum(TERMINAL_MODES),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  slaOverrideMinutes: z.number().int().min(1).nullable(),
});

type StageFormValues = z.infer<typeof stageSchema>;

function asNullableNumber(value: string) {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function compareValues(a: string | number, b: string | number) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function getStageTypeLabel(stage: LeadStage) {
  return TERMINAL_MODE_LABELS[getTerminalMode(stage)];
}

function getSortValue(column: StageColumn, stage: LeadStage) {
  switch (column) {
    case 'stage':
      return stage.name.toLowerCase();
    case 'type':
      return getStageTypeLabel(stage).toLowerCase();
    case 'default':
      return stage.isDefault ? 1 : 0;
    case 'active':
      return stage.isActive ? 1 : 0;
    case 'sla':
      return stage.slaOverrideMinutes ?? 0;
    case 'order':
      return stage.sortOrder;
  }
}

function boolText(value: boolean) {
  return value ? 'Enabled' : 'Disabled';
}

function breachActionLabel(value: LeadSettings['slaBreachAction']) {
  return value === 'REASSIGN' ? 'Reassign' : 'Notify only';
}

function userLabel(user: {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  email?: string | null;
}) {
  return (
    user.displayName?.trim() ||
    [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
    user.email ||
    'Unknown user'
  );
}

// Exported for its test: the route component itself is code-split and lazy.
export function LeadSettingsPage() {
  const router = useRouter();
  const searchParams = Route.useSearch();
  const settingsQuery = useLeadSettings();
  const stagesQuery = useLeadStages();
  // Company-wide lead owners (not scope-filtered by the actor's manual
  // assignment authority) so a configurer — incl. a TEAM-scoped sales manager —
  // can pick any valid fallback owner, and without needing `users:view`.
  const usersQuery = useLeadOwners({ enabled: true });
  const updateSettings = useUpdateLeadSettings();
  // PUT /lead-settings now requires `leads:configure` — gate all config controls
  // (the settings drawer's edit button + the routing/availability editors).
  const canConfigure = useHasPermission('leads:configure');
  const canViewLeads = useHasPermission('leads:view');
  const canCreateLeads = useHasPermission('leads:create');
  const createStage = useCreateLeadStage();
  const updateStage = useUpdateLeadPipelineStage();
  const deleteStage = useDeleteLeadStage();
  const reorderStages = useReorderLeadStages();

  const settings = settingsQuery.data ?? DEFAULT_SETTINGS;
  const stages = useMemo(
    () => [...(stagesQuery.data ?? [])].sort((left, right) => left.sortOrder - right.sortOrder),
    [stagesQuery.data],
  );
  const activeUsers = useMemo(
    () => (usersQuery.data?.data ?? []).filter((user) => user.status === 'active'),
    [usersQuery.data?.data],
  );
  const fallbackUser = useMemo(
    () => activeUsers.find((user) => user.id === settings.fallbackUserId),
    [activeUsers, settings.fallbackUserId],
  );

  const [stageSearch, setStageSearch] = useState('');
  const [stageTypeFilter, setStageTypeFilter] = useState<StageTypeFilter>('all');
  const [stageStatusFilter, setStageStatusFilter] = useState<StageStatusFilter>('all');
  const [sortKey, setSortKey] = useState<StageColumn | null>('order');
  const [sortDir, setSortDir] = useState<SettingsDataGridSortDir>('asc');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<LeadStage | null>(null);

  const editStage = useMemo(
    () => stages.find((stage) => stage.id === searchParams.stageId) ?? null,
    [searchParams.stageId, stages],
  );

  const filteredStages = useMemo(() => {
    const query = stageSearch.trim().toLowerCase();
    return stages.filter((stage) => {
      const mode = getTerminalMode(stage);
      const matchesSearch =
        !query ||
        [
          stage.name,
          getStageTypeLabel(stage),
          stage.slaOverrideMinutes ? `${stage.slaOverrideMinutes}m` : '',
        ]
          .join(' ')
          .toLowerCase()
          .includes(query);
      const matchesType = stageTypeFilter === 'all' || mode === stageTypeFilter;
      const matchesStatus =
        stageStatusFilter === 'all' ||
        (stageStatusFilter === 'active' && stage.isActive) ||
        (stageStatusFilter === 'inactive' && !stage.isActive);
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [stageSearch, stageStatusFilter, stageTypeFilter, stages]);

  const sortedStages = useMemo(() => {
    if (!sortKey) return filteredStages;
    return [...filteredStages].sort((left, right) => {
      const result = compareValues(getSortValue(sortKey, left), getSortValue(sortKey, right));
      return sortDir === 'asc' ? result : -result;
    });
  }, [filteredStages, sortDir, sortKey]);

  const activeFilterCount =
    (stageTypeFilter !== 'all' ? 1 : 0) + (stageStatusFilter !== 'all' ? 1 : 0);
  const totalPages = Math.max(1, Math.ceil(sortedStages.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStages = sortedStages.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const settingsOpen = searchParams.drawer === 'settings';
  const stageDrawerMode =
    searchParams.drawer === 'stage-new'
      ? 'create'
      : searchParams.drawer === 'stage-detail'
        ? 'detail'
        : 'edit';
  const stageDrawerOpen =
    searchParams.drawer === 'stage-new' ||
    searchParams.drawer === 'stage-edit' ||
    searchParams.drawer === 'stage-detail';
  const visibleStageDrawerOpen = stageDrawerOpen && (canConfigure || stageDrawerMode === 'detail');
  const stagePending = createStage.isPending || updateStage.isPending;

  function openSettingsDrawer() {
    if (!canConfigure) return;
    void router.navigate({ to: '/dashboard/settings/leads', search: { drawer: 'settings' } });
  }

  function openCreateStageDrawer() {
    if (!canConfigure) return;
    void router.navigate({ to: '/dashboard/settings/leads', search: { drawer: 'stage-new' } });
  }

  function openDetailDrawer(stageId: string) {
    void router.navigate({
      to: '/dashboard/settings/leads',
      search: { drawer: 'stage-detail', stageId },
    });
  }

  function openEditStageDrawer(stageId: string) {
    if (!canConfigure) return;
    void router.navigate({
      to: '/dashboard/settings/leads',
      search: { drawer: 'stage-edit', stageId },
    });
  }

  function closeDrawer() {
    void router.navigate({ to: '/dashboard/settings/leads', search: {} });
  }

  function resetStageFilters() {
    setStageSearch('');
    setStageTypeFilter('all');
    setStageStatusFilter('all');
    setPage(1);
  }

  function handleSort(column: SettingsDataGridColumn<StageColumn>, dir: SettingsDataGridSortDir) {
    if (!column.sortable) return;
    setSortKey(column.key);
    setSortDir(dir);
    setPage(1);
  }

  function moveStage(stage: LeadStage, direction: 'up' | 'down') {
    if (!canConfigure) return;
    const currentIndex = stages.findIndex((item) => item.id === stage.id);
    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= stages.length) return;
    const reordered = [...stages];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(nextIndex, 0, moved);
    reorderStages.mutate({
      stages: reordered.map((item, index) => ({ id: item.id, sortOrder: index + 1 })),
    });
  }

  function handleSettingsSave(values: LeadSettingsFormValues) {
    if (!canConfigure) return;
    updateSettings.mutate(values, { onSuccess: closeDrawer });
  }

  function handleStageSave(values: StageFormValues) {
    if (!canConfigure) return;
    const payload = {
      name: values.name.trim(),
      color: values.color,
      isDefault: values.isDefault,
      ...terminalFlags(values.terminalMode),
      slaOverrideMinutes: values.slaOverrideMinutes,
    };

    if (stageDrawerMode === 'create') {
      createStage.mutate(
        {
          ...payload,
          sortOrder: stages.length + 1,
        },
        { onSuccess: closeDrawer },
      );
      return;
    }

    if (!editStage) return;
    updateStage.mutate(
      {
        id: editStage.id,
        ...payload,
        isActive: values.isActive,
      },
      { onSuccess: closeDrawer },
    );
  }

  function renderStageCell(key: StageColumn, stage: LeadStage) {
    switch (key) {
      case 'stage':
        return (
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="h-5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: stage.color }}
              aria-hidden
            />
            <p className="truncate font-medium">{stage.name}</p>
          </div>
        );
      case 'type':
        return <StageTypeBadge stage={stage} />;
      case 'default':
        return <BoolIcon value={stage.isDefault} />;
      case 'active':
        return (
          <StatusChip
            status={stage.isActive ? 'ACTIVE' : 'INACTIVE'}
            label={stage.isActive ? 'Active' : 'Inactive'}
            variant="table"
          />
        );
      case 'sla':
        return stage.slaOverrideMinutes ? `${stage.slaOverrideMinutes}m` : 'Default';
      case 'order':
        return <span className="tabular-nums">{stage.sortOrder}</span>;
    }
  }

  // Drawer selects write to a draft; committed type/status still drive the list
  // and the activeFilterCount badge. Search stays instant (not drafted).
  const committedFilters = useMemo<StageFilterDraft>(
    () => ({ search: stageSearch, type: stageTypeFilter, status: stageStatusFilter }),
    [stageSearch, stageTypeFilter, stageStatusFilter],
  );
  const filterDraft = useFilterDraft(
    committedFilters,
    (next) => {
      setStageSearch(next.search);
      setStageTypeFilter(next.type);
      setStageStatusFilter(next.status);
      setPage(1);
    },
    STAGE_FILTER_DEFAULTS,
  );
  const { draft } = filterDraft;
  const draftFilterCount =
    (draft.search.trim() ? 1 : 0) +
    (draft.type !== 'all' ? 1 : 0) +
    (draft.status !== 'all' ? 1 : 0);

  const filterContent = (
    <div className="space-y-5 py-5">
      <DataTableFilterSection
        label="Stage"
        active={Boolean(draft.search)}
        onClear={() => filterDraft.setDraft({ search: '' })}
      >
        <DataTableSearchFilterField
          value={draft.search}
          placeholder="Search stages..."
          onValueChange={(value) => filterDraft.setDraft({ search: value })}
        />
      </DataTableFilterSection>
      <DataTableFilterSection
        label="Usage"
        active={draft.type !== 'all' || draft.status !== 'all'}
        onClear={() => filterDraft.setDraft({ type: 'all', status: 'all' })}
      >
        <div className="space-y-3">
          <DataTableSelectFilterField
            value={draft.type}
            placeholder="Type"
            options={STAGE_TYPE_OPTIONS}
            onValueChange={(value) => filterDraft.setDraft({ type: value as StageTypeFilter })}
          />
          <DataTableSelectFilterField
            value={draft.status}
            placeholder="Status"
            options={STAGE_STATUS_OPTIONS}
            onValueChange={(value) => filterDraft.setDraft({ status: value as StageStatusFilter })}
          />
        </div>
      </DataTableFilterSection>
    </div>
  );

  if (searchParams.tab === 'test') {
    return (
      <DetailPageLayout
        header={<LeadsEngineTabs active="test" showTest={canViewLeads} />}
        contentClassName="px-4 py-4 md:px-5 md:py-5"
      >
        <Fullbleed />
        {canViewLeads ? (
          <LeadTestIntakePanel canCreateLead={canCreateLeads} />
        ) : (
          <RoutePermissionDenied message="You do not have permission to view leads." />
        )}
      </DetailPageLayout>
    );
  }

  return (
    <DetailPageLayout
      header={
        <>
          <LeadsEngineTabs active="configuration" showTest={canViewLeads} />
          <Button
            size="icon"
            variant="outline"
            onClick={openSettingsDrawer}
            aria-label="Edit lead settings"
            disabled={!canConfigure}
            title={canConfigure ? undefined : 'Requires the Configure leads permission'}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </>
      }
      contentClassName="px-4 py-4 md:px-5 md:py-5"
    >
      <Fullbleed />

      <div className="space-y-5">
        <div className="grid gap-4 xl:grid-cols-4">
          <ReadOnlyCard icon={Clock} title="Follow-up">
            {settingsQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="space-y-3">
                <DetailRow label="Auto-set follow-up" value={boolText(settings.autoSetFollowUp)} />
                <DetailRow label="Default timing" value={`${settings.defaultFollowUpDays} days`} />
              </div>
            )}
          </ReadOnlyCard>

          <ReadOnlyCard icon={Target} title="Speed-to-Lead">
            {settingsQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="space-y-3">
                <DetailRow label="Default" value={`${settings.defaultSlaMinutes}m`} />
                <DetailRow label="Urgent" value={`${settings.slaUrgentMinutes}m`} />
                <DetailRow label="High" value={`${settings.slaHighMinutes}m`} />
                <DetailRow label="Low" value={`${settings.slaLowMinutes}m`} />
                <DetailRow
                  label="Warning"
                  value={`${settings.slaWarningThresholdPercent}% elapsed`}
                />
                <DetailRow
                  label="Breach action"
                  value={breachActionLabel(settings.slaBreachAction)}
                />
                <DetailRow
                  label="Source overrides"
                  value={
                    settings.sourceSlaOverrides.length > 0
                      ? `${settings.sourceSlaOverrides.length} configured`
                      : 'None'
                  }
                />
              </div>
            )}
          </ReadOnlyCard>

          <ReadOnlyCard icon={Bell} title="Notifications">
            {settingsQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="space-y-3">
                <DetailRow label="New lead" value={boolText(settings.notifyOnNewLead)} />
                <DetailRow label="SLA warning" value={boolText(settings.notifyOnSlaWarning)} />
                <DetailRow label="SLA breach" value={boolText(settings.notifyOnSlaBreach)} />
              </div>
            )}
          </ReadOnlyCard>

          <ReadOnlyCard icon={Users} title="Assignment">
            {settingsQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="space-y-3">
                <DetailRow label="Auto-assign" value={boolText(settings.autoAssignEnabled)} />
                <DetailRow
                  label="Fallback user"
                  value={fallbackUser ? userLabel(fallbackUser) : 'None'}
                />
                <DetailRow
                  label="Escalation user"
                  value={
                    settings.slaEscalationUserId
                      ? userLabel(
                          activeUsers.find((user) => user.id === settings.slaEscalationUserId) ?? {
                            email: settings.slaEscalationUserId,
                          },
                        )
                      : 'None'
                  }
                />
                <DetailRow label="Rep capacity" value={`${settings.repCapacityLimit} open leads`} />
              </div>
            )}
          </ReadOnlyCard>
        </div>

        <div className="space-y-1 pt-1">
          <h2 className="text-sm font-semibold">Pipeline stages</h2>
          <p className="text-sm text-muted-foreground">
            The columns leads move through, from first contact to Converted or Lost. Reorder them to
            match your sales process, or add a stage. Each lead always sits in exactly one stage.
          </p>
        </div>

        {/* The grid uses flex-1/h-full to fill its parent, but this settings page is a
            plain scrolling column (many sections), so it needs an explicitly bounded
            height — otherwise the AG Grid viewport collapses to 0px and no rows show. */}
        <div className="flex h-[32rem] min-h-0 flex-col">
          <SettingsDataGrid
            columns={STAGE_COLUMNS}
            rows={pageStages}
            getRowKey={(stage) => stage.id}
            renderCell={renderStageCell}
            storageKey="settings-lead-stages"
            emptyIcon={GitBranch}
            emptyTitle="No lead stages yet"
            searchValue={stageSearch}
            onSearchChange={(value) => {
              setStageSearch(value);
              setPage(1);
            }}
            searchPlaceholder="Search stages..."
            showClearAll={Boolean(stageSearch) || activeFilterCount > 0}
            onClearAll={resetStageFilters}
            activeFilterCount={activeFilterCount}
            filterSheet={{
              title: 'Filters',
              content: filterContent,
              onReset: filterDraft.reset,
              resetDisabled: draftFilterCount === 0,
              onApply: filterDraft.apply,
              onOpen: filterDraft.seed,
            }}
            toolbarActions={
              canConfigure ? (
                <Button size="sm" onClick={openCreateStageDrawer}>
                  <Plus className="h-4 w-4" />
                  Add stage
                </Button>
              ) : undefined
            }
            showExport={false}
            isLoading={stagesQuery.isLoading}
            onRowClick={(stage) => openDetailDrawer(stage.id)}
            rowClassName="cursor-pointer"
            renderRowActions={
              canConfigure
                ? (stage) => (
                    <StageRowActions
                      stage={stage}
                      canMoveUp={stages.findIndex((item) => item.id === stage.id) > 0}
                      canMoveDown={
                        stages.findIndex((item) => item.id === stage.id) < stages.length - 1
                      }
                      onEdit={() => openEditStageDrawer(stage.id)}
                      onMoveUp={() => moveStage(stage, 'up')}
                      onMoveDown={() => moveStage(stage, 'down')}
                      onDelete={() => setDeleteTarget(stage)}
                    />
                  )
                : undefined
            }
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
            pagination={{
              count: sortedStages.length,
              singular: 'lead stage',
              page: currentPage,
              totalPages,
              onPrevious: () => setPage((value) => Math.max(1, value - 1)),
              onNext: () => setPage((value) => Math.min(totalPages, value + 1)),
            }}
          />
        </div>

        {!canConfigure ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-300">
            You can view lead settings, routing rules, and rep availability, but changing them
            requires the <span className="font-medium">Configure leads</span> permission.
          </div>
        ) : null}

        <AcquisitionChannelSettingsSection canConfigure={canConfigure} />

        <LeadRoutingRulesSection settings={settings} canConfigure={canConfigure} />

        <LeadRepAvailabilitySection canConfigure={canConfigure} />
      </div>

      <LeadSettingsDrawer
        open={settingsOpen && canConfigure}
        settings={settings}
        users={activeUsers}
        isPending={updateSettings.isPending}
        onClose={closeDrawer}
        onSave={handleSettingsSave}
      />

      <LeadStageDrawer
        open={visibleStageDrawerOpen}
        mode={stageDrawerMode}
        stage={editStage}
        canConfigure={canConfigure}
        isPending={stagePending}
        onClose={closeDrawer}
        onSave={handleStageSave}
        onEdit={() => editStage && openEditStageDrawer(editStage.id)}
        onBack={
          searchParams.drawer === 'stage-edit' && editStage
            ? () => openDetailDrawer(editStage.id)
            : undefined
        }
      />

      <Dialog
        open={Boolean(deleteTarget) && canConfigure}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete lead stage?</DialogTitle>
            <DialogDescription>
              Stages with leads cannot be deleted. Reassign those leads first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!deleteTarget || !canConfigure) return;
                deleteStage.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DetailPageLayout>
  );
}

function ReadOnlyCard({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Clock;
  title: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
            <Icon className="h-4 w-4 text-muted-foreground" />
          </span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function CardSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-5 w-5/6" />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

function BoolIcon({ value }: { value: boolean }) {
  return value ? (
    <Check className="mx-auto h-4 w-4 text-green-600" />
  ) : (
    <X className="mx-auto h-4 w-4 text-muted-foreground/40" />
  );
}

function StageTypeBadge({ stage }: { stage: LeadStage }) {
  const mode = getTerminalMode(stage);
  // Label from the shared map, not a second copy of it. The row search builds
  // its haystack from `getStageTypeLabel` and the type column sorts on it, so a
  // badge wording its own label drifts into a screen that filters by one word
  // and displays another.
  const label = TERMINAL_MODE_LABELS[mode];
  if (mode === 'won') return <Badge className="bg-green-100 text-green-800">{label}</Badge>;
  if (mode === 'lost') return <Badge className="bg-red-100 text-red-800">{label}</Badge>;
  // Its own treatment, not the default outline. Falling through would render a
  // closed junk column identically to an open one — muted, on a screen whose
  // whole job is showing which columns are terminal. Amber rather than the
  // loss red: it is a close, but not one that counts against a loss rate.
  if (mode === 'disqualified')
    return <Badge className="bg-amber-100 text-amber-800">{label}</Badge>;
  return <Badge variant="outline">{label}</Badge>;
}

function StageRowActions({
  stage,
  canMoveUp,
  canMoveDown,
  onEdit,
  onMoveUp,
  onMoveDown,
  onDelete,
}: {
  stage: LeadStage;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onEdit: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onMoveUp} disabled={!canMoveUp}>
          <ArrowUp className="h-3.5 w-3.5" />
          Move up
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onMoveDown} disabled={!canMoveDown}>
          <ArrowDown className="h-3.5 w-3.5" />
          Move down
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={onDelete}
          disabled={stage.isDefault || stage.isWon}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LeadSettingsDrawer({
  open,
  settings,
  users,
  isPending,
  onClose,
  onSave,
}: {
  open: boolean;
  settings: LeadSettings;
  users: Array<{
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    email?: string | null;
  }>;
  isPending: boolean;
  onClose: () => void;
  onSave: (values: LeadSettingsFormValues) => void;
}) {
  const form = useForm<LeadSettingsFormValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: settings,
    mode: 'onChange',
  });
  const [followUpOpen, setFollowUpOpen] = useState(true);
  const [slaOpen, setSlaOpen] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);

  // NOT-93: track auto-assign + fallback to drive required-state and submit-disable.
  const autoAssignEnabled = form.watch('autoAssignEnabled');
  const fallbackUserId = form.watch('fallbackUserId');
  const slaBreachAction = form.watch('slaBreachAction');
  const slaEscalationUserId = form.watch('slaEscalationUserId');
  const sourceSlaOverrides = form.watch('sourceSlaOverrides') ?? [];
  const fallbackMissing = autoAssignEnabled && !fallbackUserId;
  const reassignMissingTarget =
    slaBreachAction === 'REASSIGN' && !fallbackUserId && !slaEscalationUserId;

  function setSourceSlaOverrides(next: LeadSettingsFormValues['sourceSlaOverrides']) {
    form.setValue('sourceSlaOverrides', next, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }

  function patchSourceSlaOverride(
    index: number,
    patch: Partial<LeadSettingsFormValues['sourceSlaOverrides'][number]>,
  ) {
    setSourceSlaOverrides(
      sourceSlaOverrides.map((override, currentIndex) =>
        currentIndex === index ? { ...override, ...patch } : override,
      ),
    );
  }

  function addSourceSlaOverride() {
    const usedSources = new Set(sourceSlaOverrides.map((override) => override.source));
    const source = LEAD_SOURCES.find((item) => !usedSources.has(item.value))?.value;
    setSourceSlaOverrides([
      ...sourceSlaOverrides,
      { source: source ?? '', slaMinutes: settings.defaultSlaMinutes },
    ]);
  }

  useEffect(() => {
    if (!open) return;
    form.reset(settings);
    setFollowUpOpen(true);
    setSlaOpen(true);
    setNotificationsOpen(false);
    // Expand assignment section automatically when auto-assign is on but no
    // fallback is set, so the user sees the field they have to fill in.
    setAssignmentOpen(settings.autoAssignEnabled && !settings.fallbackUserId);
  }, [form, open, settings]);

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl"
        showCloseButton={false}
      >
        <DataTableDrawerHeader title="Edit Lead Settings" onClose={onClose} />
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSave)} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
              <FormAccordionCard
                open={followUpOpen}
                header={
                  <CollapsibleSectionHeader
                    icon={Clock}
                    label="Follow-up"
                    badge="required"
                    open={followUpOpen}
                    onToggle={() => setFollowUpOpen((current) => !current)}
                  />
                }
              >
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="autoSetFollowUp"
                    render={({ field }) => (
                      <ToggleCard
                        label="Auto-set follow-up date"
                        description="Automatically set a follow-up date when a new lead is created."
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="defaultFollowUpDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default follow-up days</FormLabel>
                        <FormControl>
                          <NumberInput
                            min={1}
                            max={90}
                            value={field.value}
                            emptyValue={1}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </FormAccordionCard>

              <FormAccordionCard
                open={slaOpen}
                header={
                  <CollapsibleSectionHeader
                    icon={Target}
                    label="Speed-to-Lead"
                    badge="required"
                    open={slaOpen}
                    onToggle={() => setSlaOpen((current) => !current)}
                  />
                }
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <NumberField
                    control={form.control}
                    name="defaultSlaMinutes"
                    label="Default SLA"
                  />
                  <NumberField control={form.control} name="slaUrgentMinutes" label="Urgent SLA" />
                  <NumberField control={form.control} name="slaHighMinutes" label="High SLA" />
                  <NumberField control={form.control} name="slaLowMinutes" label="Low SLA" />
                  <NumberField
                    control={form.control}
                    name="slaWarningThresholdPercent"
                    label="Warning threshold (%)"
                    max={99}
                  />
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="slaBreachAction"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Breach action</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="NOTIFY_ONLY">Notify only</SelectItem>
                            <SelectItem value="REASSIGN">Reassign</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="slaEscalationUserId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Escalation user
                          {slaBreachAction === 'REASSIGN' && !fallbackUserId ? (
                            <span className="ml-1 text-destructive" aria-hidden>
                              *
                            </span>
                          ) : null}
                        </FormLabel>
                        <Select
                          value={field.value ?? NO_ESCALATION_VALUE}
                          onValueChange={(value) =>
                            field.onChange(value === NO_ESCALATION_VALUE ? null : value)
                          }
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={NO_ESCALATION_VALUE}>No escalation user</SelectItem>
                            {users.map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {userLabel(user)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="mt-5 space-y-3 border-t pt-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-medium">Source SLA overrides</h4>
                      <p className="text-xs text-muted-foreground">
                        Source minutes override priority minutes for new leads.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSourceSlaOverride}
                    >
                      <Plus className="h-4 w-4" />
                      Add source
                    </Button>
                  </div>
                  {sourceSlaOverrides.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No source overrides.</p>
                  ) : (
                    <div className="space-y-2">
                      {sourceSlaOverrides.map((override, index) => {
                        const rowError = form.formState.errors.sourceSlaOverrides?.[index];
                        return (
                          <div
                            key={`${override.source}-${index}`}
                            className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_120px_36px]"
                          >
                            <div className="min-w-0">
                              <Select
                                value={override.source}
                                onValueChange={(value) =>
                                  patchSourceSlaOverride(index, { source: value })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Source" />
                                </SelectTrigger>
                                <SelectContent>
                                  {override.source &&
                                    !LEAD_SOURCES.some(
                                      (source) => source.value === override.source,
                                    ) && (
                                      <SelectItem value={override.source}>
                                        {leadSourceLabel(override.source)}
                                      </SelectItem>
                                    )}
                                  {LEAD_SOURCES.map((source) => (
                                    <SelectItem key={source.value} value={source.value}>
                                      {source.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {rowError?.source?.message ? (
                                <p className="mt-1 text-xs text-destructive">
                                  {rowError.source.message}
                                </p>
                              ) : null}
                            </div>
                            <div>
                              <NumberInput
                                min={1}
                                value={override.slaMinutes}
                                emptyValue={1}
                                onChange={(next) =>
                                  patchSourceSlaOverride(index, { slaMinutes: next ?? 1 })
                                }
                                aria-label={`${leadSourceLabel(override.source)} SLA minutes`}
                              />
                              {rowError?.slaMinutes?.message ? (
                                <p className="mt-1 text-xs text-destructive">
                                  {rowError.slaMinutes.message}
                                </p>
                              ) : null}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9"
                              onClick={() =>
                                setSourceSlaOverrides(
                                  sourceSlaOverrides.filter(
                                    (_, currentIndex) => currentIndex !== index,
                                  ),
                                )
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </FormAccordionCard>

              <FormOptionalSectionDivider />

              <FormAccordionCard
                open={notificationsOpen}
                header={
                  <CollapsibleSectionHeader
                    icon={Bell}
                    label="Notifications"
                    badge="optional"
                    open={notificationsOpen}
                    onToggle={() => setNotificationsOpen((current) => !current)}
                  />
                }
              >
                <div className="space-y-3">
                  <FormField
                    control={form.control}
                    name="notifyOnNewLead"
                    render={({ field }) => (
                      <ToggleCard
                        label="New lead"
                        description="Notify assigned reps when a new lead arrives."
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="notifyOnSlaWarning"
                    render={({ field }) => (
                      <ToggleCard
                        label="SLA warning"
                        description="Alert when the SLA is close to being missed."
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="notifyOnSlaBreach"
                    render={({ field }) => (
                      <ToggleCard
                        label="SLA breach"
                        description="Alert when the SLA deadline is missed."
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </FormAccordionCard>

              <FormAccordionCard
                open={assignmentOpen}
                header={
                  <CollapsibleSectionHeader
                    icon={Users}
                    label="Assignment"
                    badge="optional"
                    open={assignmentOpen}
                    onToggle={() => setAssignmentOpen((current) => !current)}
                  />
                }
              >
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="autoAssignEnabled"
                    render={({ field }) => (
                      <ToggleCard
                        label="Auto-assign new leads"
                        description="Assign new leads to the contact owner or fallback user."
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="fallbackUserId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Fallback user
                          {autoAssignEnabled ? (
                            <span className="ml-1 text-destructive" aria-hidden>
                              *
                            </span>
                          ) : null}
                        </FormLabel>
                        {/*
                          NOT-93: When auto-assign is on, a fallback user is required.
                          We intentionally do NOT render a "No fallback user" / clear
                          option — once the dropdown is exposed, a real user must be
                          chosen, and previously-set fallbacks cannot be removed.
                        */}
                        <Select
                          value={field.value ?? undefined}
                          onValueChange={(value) => field.onChange(value)}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a fallback user" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {users.map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {userLabel(user)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {autoAssignEnabled ? (
                          <p className="text-xs text-muted-foreground">
                            Required while auto-assign is enabled.
                          </p>
                        ) : null}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <NumberField
                    control={form.control}
                    name="repCapacityLimit"
                    label="Max open leads per rep"
                  />
                  <FormField
                    control={form.control}
                    name="honorWeeklyHours"
                    render={({ field }) => (
                      <ToggleCard
                        label="Honor weekly working hours"
                        description="Also skip reps who are outside their configured working hours (set per rep below), not just those manually marked unavailable."
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </FormAccordionCard>
            </div>
            <SheetFooter className="flex-row justify-end border-t px-5 py-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              {/* NOT-93: block save client-side when fallback is required but missing. */}
              <Button
                type="submit"
                disabled={isPending || fallbackMissing || reassignMissingTarget}
              >
                {isPending ? 'Saving...' : 'Save'}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}

function NumberField({
  control,
  name,
  label,
  max,
}: {
  control: ReturnType<typeof useForm<LeadSettingsFormValues>>['control'];
  name:
    | 'defaultFollowUpDays'
    | 'defaultSlaMinutes'
    | 'slaUrgentMinutes'
    | 'slaHighMinutes'
    | 'slaLowMinutes'
    | 'slaWarningThresholdPercent'
    | 'repCapacityLimit';
  label: string;
  max?: number;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <NumberInput
              min={1}
              max={max}
              value={field.value}
              emptyValue={1}
              onChange={field.onChange}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function StageDetailContent({
  stage,
  canConfigure,
  onEdit,
  onClose,
}: {
  stage: LeadStage;
  canConfigure: boolean;
  onEdit?: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <SheetHeader className="border-b px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="h-4 w-4 shrink-0 rounded-sm"
              style={{ backgroundColor: stage.color }}
              aria-hidden
            />
            <SheetTitle className="text-sm font-semibold">{stage.name}</SheetTitle>
          </div>
          <div className="flex items-center gap-2">
            {canConfigure ? (
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                title="Edit"
                onClick={onEdit}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            ) : null}
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X size={16} />
            </button>
          </div>
        </div>
      </SheetHeader>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
        <FormAccordionCard
          open
          header={
            <CollapsibleSectionHeader
              icon={GitBranch}
              label="Details"
              open
              onToggle={() => undefined}
            />
          }
        >
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Name</span>
              <span className="font-medium">{stage.name}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Type</span>
              <StageTypeBadge stage={stage} />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Order</span>
              <span>{stage.sortOrder}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Default</span>
              <span>{stage.isDefault ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Active</span>
              <span>{stage.isActive ? 'Yes' : 'No'}</span>
            </div>
            {stage.slaOverrideMinutes ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">SLA Override</span>
                <span>{stage.slaOverrideMinutes}m</span>
              </div>
            ) : null}
          </div>
        </FormAccordionCard>
      </div>
    </>
  );
}

function LeadStageDrawer({
  open,
  mode,
  stage,
  canConfigure,
  isPending,
  onClose,
  onSave,
  onEdit,
  onBack,
}: {
  open: boolean;
  mode: 'create' | 'edit' | 'detail';
  stage: LeadStage | null;
  canConfigure: boolean;
  isPending: boolean;
  onClose: () => void;
  onSave: (values: StageFormValues) => void;
  onEdit?: () => void;
  onBack?: () => void;
}) {
  const isEdit = mode === 'edit';
  const form = useForm<StageFormValues>({
    resolver: zodResolver(stageSchema),
    defaultValues: emptyStageFormValues(),
  });
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [behaviorOpen, setBehaviorOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDetailsOpen(true);
    setBehaviorOpen(false);
    form.reset(stage && isEdit ? stageToFormValues(stage) : emptyStageFormValues());
  }, [form, isEdit, open, stage]);

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
        showCloseButton={false}
      >
        {mode === 'detail' ? (
          stage ? (
            <StageDetailContent
              stage={stage}
              canConfigure={canConfigure}
              onEdit={onEdit}
              onClose={onClose}
            />
          ) : (
            <>
              <DataTableDrawerHeader title="Lead Stage" onClose={onClose} />
              <div className="px-5 py-5 text-sm text-muted-foreground">Lead stage not found.</div>
            </>
          )
        ) : (
          <>
            <DataTableDrawerHeader
              title={isEdit ? 'Edit Lead Stage' : 'Create Lead Stage'}
              onClose={onClose}
              onBack={onBack}
            />
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSave)} className="flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
                  {isEdit && !stage ? (
                    <p className="text-sm text-muted-foreground">Lead stage not found.</p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        A stage is a column in your leads pipeline — leads move through stages from
                        first contact to Converted or Lost. Only a name and color are required; the
                        defaults below work for most teams.
                      </p>
                      <FormAccordionCard
                        open={detailsOpen}
                        header={
                          <CollapsibleSectionHeader
                            icon={GitBranch}
                            label="Details"
                            badge="required"
                            open={detailsOpen}
                            onToggle={() => setDetailsOpen((current) => !current)}
                          />
                        }
                      >
                        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_120px]">
                          <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Name</FormLabel>
                                <FormControl>
                                  <Input placeholder="e.g. Contacted" {...field} />
                                </FormControl>
                                <FormDescription>
                                  What reps see as the board column and on each lead card.
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="color"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Color</FormLabel>
                                <FormControl>
                                  <Input type="color" className="h-10 p-1" {...field} />
                                </FormControl>
                                <FormDescription>
                                  Color of the column header and status chip.
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </FormAccordionCard>

                      <FormOptionalSectionDivider />

                      <FormAccordionCard
                        open={behaviorOpen}
                        header={
                          <CollapsibleSectionHeader
                            icon={Target}
                            label="Behavior"
                            badge="optional"
                            open={behaviorOpen}
                            onToggle={() => setBehaviorOpen((current) => !current)}
                          />
                        }
                      >
                        <div className="space-y-4">
                          <FormField
                            control={form.control}
                            name="terminalMode"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Stage type</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {TERMINAL_MODES.map((mode) => (
                                      <SelectItem key={mode} value={mode}>
                                        {TERMINAL_MODE_LABELS[mode]} —{' '}
                                        {TERMINAL_MODE_DESCRIPTIONS[mode]}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormDescription>
                                  <span className="font-medium">{TERMINAL_MODE_LABELS.open}</span>{' '}
                                  stages are in-progress.{' '}
                                  <span className="font-medium">{TERMINAL_MODE_LABELS.won}</span>{' '}
                                  closes the lead as a sale (leads here can convert to an estimate
                                  or job).{' '}
                                  <span className="font-medium">{TERMINAL_MODE_LABELS.lost}</span>{' '}
                                  closes it without a sale (a loss reason is required).{' '}
                                  <span className="font-medium">
                                    {TERMINAL_MODE_LABELS.disqualified}
                                  </span>{' '}
                                  also closes it without a sale, for leads that were never real work
                                  — spam or a duplicate. Keeping those two apart is what stops junk
                                  counting against your loss rate. Keep one of each terminal.
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <div className="grid gap-3 sm:grid-cols-2">
                            <FormField
                              control={form.control}
                              name="isDefault"
                              render={({ field }) => (
                                <ToggleCard
                                  label="Default stage"
                                  description="Every new lead starts here. Exactly one stage is the default."
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              )}
                            />
                            {isEdit ? (
                              <FormField
                                control={form.control}
                                name="isActive"
                                render={({ field }) => (
                                  <ToggleCard
                                    label="Active"
                                    description="Available in lead workflows."
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                )}
                              />
                            ) : null}
                          </div>
                          <FormField
                            control={form.control}
                            name="slaOverrideMinutes"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>SLA override minutes</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={1}
                                    placeholder="Use global SLA"
                                    value={field.value ?? ''}
                                    onChange={(event) =>
                                      field.onChange(asNullableNumber(event.target.value))
                                    }
                                  />
                                </FormControl>
                                <FormDescription>
                                  Leave blank to use your global Speed-to-Lead SLA. Set minutes to
                                  give leads in this stage a different first-response deadline.
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </FormAccordionCard>
                    </>
                  )}
                </div>
                <SheetFooter className="flex-row justify-end border-t px-5 py-4">
                  <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isPending || (isEdit && !stage)}>
                    {isPending
                      ? isEdit
                        ? 'Saving...'
                        : 'Creating...'
                      : isEdit
                        ? 'Save'
                        : 'Create'}
                  </Button>
                </SheetFooter>
              </form>
            </Form>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ToggleCard({
  label,
  description,
  checked,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border px-4 py-3">
      <div className="min-w-0">
        <p className="font-medium">{label}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={label} />
    </div>
  );
}

function emptyStageFormValues(): StageFormValues {
  return {
    name: '',
    color: '#2196F3',
    terminalMode: 'open',
    isDefault: false,
    isActive: true,
    slaOverrideMinutes: null,
  };
}

function stageToFormValues(stage: LeadStage): StageFormValues {
  return {
    name: stage.name,
    color: stage.color,
    terminalMode: getTerminalMode(stage),
    isDefault: stage.isDefault,
    isActive: stage.isActive,
    slaOverrideMinutes: stage.slaOverrideMinutes ?? null,
  };
}
